/**
 * MessagesHealthChip — Iter-176 · Phase 8 + Iter-178 · Phase 13.
 *
 * Read-only "Messages Health" chip on the NECS Admin dashboard.
 * Shows 24h + 7d retry-event totals with GREEN / AMBER / RED status.
 * Clicking the chip toggles a 7-day retry sparkline panel with the
 * top error types + channels + last retry timestamp.
 */
import { useEffect, useState } from 'react';
import { MessageSquareWarning, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const COLOR = {
  green: { bg: '#166534', dot: '#22c55e', text: 'HEALTHY' },
  amber: { bg: '#78350F', dot: '#f59e0b', text: 'WATCH' },
  red:   { bg: '#7F1D1D', dot: '#ef4444', text: 'ATTENTION' },
};

function Sparkline({ series }) {
  if (!series || series.length === 0) return null;
  const max = Math.max(1, ...series.map((p) => p.count || 0));
  const w = 220, h = 40, pad = 4;
  const step = (w - pad * 2) / Math.max(1, series.length - 1);
  const pts = series.map((p, i) => {
    const x = pad + i * step;
    const y = h - pad - ((p.count || 0) / max) * (h - pad * 2);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} data-testid="messages-health-sparkline">
      <polyline
        points={pts}
        fill="none"
        stroke="#C9A962"
        strokeWidth="2"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      {series.map((p, i) => (
        <circle key={i}
                cx={pad + i * step}
                cy={h - pad - ((p.count || 0) / max) * (h - pad * 2)}
                r={2.5} fill="#C9A962" />
      ))}
    </svg>
  );
}

export default function MessagesHealthChip() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [history, setHistory] = useState(null);

  useEffect(() => {
    let alive = true;
    let stopped = false;
    const load = async () => {
      if (stopped) return;
      try {
        const r = await fetch(`${API}/api/admin/messages-health`,
                               { credentials: 'include' });
        if (r.status === 403 || r.status === 401) { stopped = true; return; }
        if (r.ok && alive) setData(await r.json());
      } catch (_) { /* silent */ }
    };
    load();
    const t = setInterval(load, 60000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  useEffect(() => {
    if (!open || history) return;
    (async () => {
      try {
        const r = await fetch(`${API}/api/admin/messages-health/history`,
                               { credentials: 'include' });
        if (r.ok) setHistory(await r.json());
      } catch (_) { /* silent */ }
    })();
  }, [open, history]);

  if (!data) return null;
  const style = COLOR[data.status] || COLOR.green;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center justify-center gap-1 sm:gap-2 min-h-9 px-2 sm:px-3 py-1.5 rounded-full text-xs font-bold hover:opacity-90 transition-opacity"
        style={{ backgroundColor: style.bg, color: '#fff' }}
        title={`Messages retries — 24h: ${data.retries_24h} · 7d: ${data.retries_7d}`}
        aria-label={`Messages ${style.text}. Retries: ${data.retries_24h} in 24 hours; ${data.retries_7d} in 7 days.`}
        data-testid="messages-health-chip"
        data-status={data.status}
        aria-expanded={open}
      >
        <span
          className="h-2 w-2 rounded-full inline-block"
          style={{ backgroundColor: style.dot }}
          aria-hidden="true"
        />
        <MessageSquareWarning className="h-3.5 w-3.5" />
        <span className="hidden sm:inline uppercase">Messages · {style.text}</span>
        <span className="hidden md:inline opacity-90">
          24h: <b data-testid="messages-health-24h">{data.retries_24h}</b>
          {' · '}
          7d: <b data-testid="messages-health-7d">{data.retries_7d}</b>
        </span>
      </button>

      {open && (
        <div
          className="fixed inset-x-4 top-24 sm:absolute sm:inset-x-auto sm:top-full sm:right-0 mt-2 w-auto sm:w-80 max-h-[70dvh] overflow-y-auto rounded-lg shadow-xl border border-zinc-700 bg-[#0a0a0a] text-white p-4 z-40"
          data-testid="messages-health-panel"
        >
          <div className="flex items-start justify-between mb-2">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-[#C9A962] font-bold">
                Messages Health · 7 days
              </p>
              <p className="text-lg font-bold" data-testid="messages-health-panel-total-7d">
                {data.retries_7d} retries
              </p>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="p-1 rounded hover:bg-zinc-800"
              aria-label="Close messages health panel"
              data-testid="messages-health-panel-close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <Sparkline series={history?.series || []} />
          <div className="mt-3">
            <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">
              Top error types
            </p>
            <ul className="text-xs mt-1 space-y-0.5"
                data-testid="messages-health-top-errors">
              {(history?.top_errors || []).slice(0, 3).map((e) => (
                <li key={e.type} className="flex items-center justify-between">
                  <span className="truncate">{e.type}</span>
                  <span className="font-bold text-[#C9A962]">{e.count}</span>
                </li>
              ))}
              {(!history || (history.top_errors || []).length === 0) && (
                <li className="italic text-zinc-500">No retries in the last 7 days.</li>
              )}
            </ul>
          </div>
          <div className="mt-3">
            <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">
              Top channels
            </p>
            <ul className="text-xs mt-1 space-y-0.5"
                data-testid="messages-health-top-channels">
              {(history?.top_channels || []).slice(0, 3).map((c) => (
                <li key={c.channel} className="flex items-center justify-between">
                  <span className="truncate">{c.channel}</span>
                  <span className="font-bold text-[#C9A962]">{c.count}</span>
                </li>
              ))}
            </ul>
          </div>
          {history?.last_retry_at && (
            <p className="mt-3 text-[10px] text-zinc-500"
               data-testid="messages-health-last-retry">
              Last retry: {new Date(history.last_retry_at).toLocaleString([],
                { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
